import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../auth/AuthContext';
import whatsappMicroserviceService from '../services/whatsappMicroserviceService';
import WhatsAppStatusCard from '../components/admin/whatsapp/WhatsAppStatusCard';
import WhatsAppQRModal from '../components/admin/whatsapp/WhatsAppQRModal';
import WhatsAppConfigForm from '../components/admin/whatsapp/WhatsAppConfigForm';
import PlantillasCitacion from '../components/admin/whatsapp/PlantillasCitacion';
import PlantillaVolverAContactar from '../components/admin/whatsapp/PlantillaVolverAContactar';
import PlantillasRecordatorioCuota from '../components/admin/whatsapp/PlantillasRecordatorioCuota';
import WhatsAppLogsTable from '../components/admin/whatsapp/WhatsAppLogsTable';
import PageHeader from '../components/ui/PageHeader';
import '../style/pages/AdminWhatsAppPage.css';

const LS_QR_MODAL_KEY = 'admin_whatsapp_qr_modal_open';

function mapStatus(health) {
  if (!health) return 'disconnected';

  const status = health.status || health?.whatsapp?.status;
  if (status) return String(status).toLowerCase();

  const connected = health.connected ?? health?.whatsapp?.connected;
  return connected ? 'connected' : 'disconnected';
}

function AdminWhatsAppPage() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState([]);
  const [config, setConfig] = useState(null);

  const [loadingHealth, setLoadingHealth] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const [savingConfig, setSavingConfig] = useState(false);
  const [loadingLogout, setLoadingLogout] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  const [qrModalOpen, setQrModalOpen] = useState(() => localStorage.getItem(LS_QR_MODAL_KEY) === '1');

  const status = useMemo(() => mapStatus(health), [health]);
  const phone = useMemo(() => health?.phone || health?.whatsapp?.phone || '', [health]);

  const fetchHealth = useCallback(async () => {
    try {
      const data = await whatsappMicroserviceService.getHealth();
      setHealth(data);
    } catch (_error) {
      toast.error('No se pudo obtener el estado de WhatsApp.');
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const data = await whatsappMicroserviceService.getLogs(200);
      setLogs(Array.isArray(data) ? data : []);
    } catch (_error) {
      toast.error('No se pudieron cargar los logs de WhatsApp.');
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await whatsappMicroserviceService.getConfig();
      setConfig(data);
    } catch (_error) {
      toast.error('No se pudo cargar la configuracion de WhatsApp.');
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    await Promise.all([fetchHealth(), fetchLogs(), fetchConfig()]);
  }, [fetchHealth, fetchLogs, fetchConfig]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    const healthPoll = setInterval(fetchHealth, 15000);
    const logsPoll = setInterval(fetchLogs, 30000);

    return () => {
      clearInterval(healthPoll);
      clearInterval(logsPoll);
    };
  }, [fetchHealth, fetchLogs]);

  useEffect(() => {
    localStorage.setItem(LS_QR_MODAL_KEY, qrModalOpen ? '1' : '0');
  }, [qrModalOpen]);

  const confirmLogout = async () => {
    try {
      setLoadingLogout(true);
      await whatsappMicroserviceService.logout();
      toast.success('Sesion de WhatsApp cerrada correctamente.');
      await fetchHealth();
      setQrModalOpen(true);
      setConfirmLogoutOpen(false);
    } catch (_error) {
      toast.error('No se pudo desconectar WhatsApp.');
    } finally {
      setLoadingLogout(false);
    }
  };

  const handleLogout = () => {
    setConfirmLogoutOpen(true);
  };

  const handleSaveConfig = async (formData) => {
    try {
      setSavingConfig(true);
      const updated = await whatsappMicroserviceService.saveConfig(formData);
      setConfig(updated);
      toast.success('Configuracion guardada correctamente.');
    } catch (_error) {
      toast.error('No se pudo guardar la configuracion.');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleOpenQr = () => {
    setQrModalOpen(true);
  };

  const handleCloseQr = () => {
    setQrModalOpen(false);
  };

  const handleConnectedFromModal = async () => {
    setQrModalOpen(false);
    await fetchHealth();
    toast.success('WhatsApp conectado correctamente.');
  };

  const adminRoles = ['ADMIN', 'PROGRAMADOR'];
  if (!adminRoles.includes(String(user?.rol || '').toUpperCase())) {
    return (
      <div className="alert alert-warning mt-3">
        Esta seccion es solo para usuarios administradores.
      </div>
    );
  }

  return (
    <div className="wa-admin-page area-admin">
      <PageHeader
        variante="plano"
        icono="whatsapp"
        titulo="Panel Admin WhatsApp"
        descripcion="Control de conexión, configuración y seguimiento de mensajes"
      />

      <div className="wa-admin-grid">
        <WhatsAppStatusCard
          status={status}
          phone={phone}
          onOpenQr={handleOpenQr}
          onLogout={handleLogout}
          loadingLogout={loadingLogout}
        />

        <WhatsAppConfigForm
          config={config}
          loading={loadingConfig}
          saving={savingConfig}
          onSave={handleSaveConfig}
        />

        <PlantillasCitacion />

        <PlantillaVolverAContactar />

        <PlantillasRecordatorioCuota />

        <WhatsAppLogsTable
          logs={logs}
          loading={loadingLogs}
          onRefresh={fetchLogs}
        />
      </div>

      <WhatsAppQRModal
        open={qrModalOpen && status !== 'connected'}
        onClose={handleCloseQr}
        onConnected={handleConnectedFromModal}
      />

      {confirmLogoutOpen && (
        <div className="wa-modal-overlay" role="dialog" aria-modal="true" aria-label="Confirmar desconexion WhatsApp">
          <div className="wa-modal-content wa-confirm-modal">
            <div className="wa-modal-header">
              <h4>Confirmar desconexion</h4>
            </div>
            <div className="wa-modal-body wa-confirm-body">
              <p>Se cerrara la sesion de WhatsApp actual. Deseas continuar?</p>
              <div className="wa-confirm-actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setConfirmLogoutOpen(false)}
                  disabled={loadingLogout}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmLogout}
                  disabled={loadingLogout}
                >
                  {loadingLogout ? 'Desconectando...' : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loadingHealth && (
        <div className="wa-loader-inline">
          <div className="spinner-border spinner-border-sm text-success" role="status" />
          Cargando estado de WhatsApp...
        </div>
      )}
    </div>
  );
}

export default AdminWhatsAppPage;
